import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { TareasFiltros, TaskService } from '../services/task/task.service';
import Task from '../../models/Task';
import TaskType from '../../models/TaskType';
import User from '../../models/User';
import { canEditGeometries } from '../../utils/CanEditGeometries';
import axios from 'axios';

export interface TaskState {
  task: Task[];
  taskTypeByUser: TaskType[];
  assignedUsers: User[];
  usersTaskAssignedTo: User[];
  usersTaskCreatedBy: User[];
  currentTask?: Task;
  showTaskModal: Boolean | undefined;
  notDataTask?: Boolean;
  canEditGeometries?: boolean;
  showNotaDeCierreModal: Boolean | undefined;
  showTaskCard: boolean; //Permite identificar que componente mostrar, si tarjetas(true) o tabla(false) atraves de query params
  filtros: TareasFiltros;
  totalItems: number;
  showFiltros: boolean;
}

const initialState: TaskState = {
  task: [],
  taskTypeByUser: [],
  assignedUsers: [],
  usersTaskAssignedTo: [],
  usersTaskCreatedBy: [],
  showTaskModal: false,
  showNotaDeCierreModal: false,
  showTaskCard: true,
  filtros: {
    search: '',
    page: 1,
    pageSize: 20,
    estado: [],
    ordenarPor: undefined,
    fechaDesde: undefined,
    tareaUsuarioEstado: [],
    usuariosAsignados: [],
    creadasPor: [],
    tipoTarea: [],
    tipoFecha: undefined,
    cancelToken: axios.CancelToken.source(),
  },
  totalItems: 0,
  showFiltros: false
};
const taskService = new TaskService();
export const taskSlice = createSlice({
  name: 'task',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {

    /**
     * setTaskFilters: Encargado de crear el array dinámico de los filtros a usar(ej.[{sort:-titulo},{search:texto}])
     * que seran usados como parametros en el get de tareas gestion con filtros.
     */
    setTaskFilters: (state: TaskState, arg: PayloadAction<Partial<TareasFiltros>>) => {
      if (arg.payload.page === undefined) {
        state.filtros = { ...state.filtros, ...arg.payload, page: 1 };
      } else {
        state.filtros = { ...state.filtros, ...arg.payload };
      }
    },
    setShowFiltrosTasks: (state: TaskState, arg: PayloadAction<boolean>) => {
      state.showFiltros = arg.payload
    },
    clearCurrentTask: (state) => {
      state.currentTask = undefined;
    },
    clearNotDataTask: (state) => {
      state.notDataTask = undefined;
    },
    setDeleteTask: (state, action: PayloadAction<number | undefined>) => {
      state.task = state.task.filter(task => task.id !== action.payload);
    },
    setShowTaskCard: (state, action: PayloadAction<boolean>) => {
      state.showTaskCard = action.payload;
    },
    refreshCanEditGeometry: state => {
      if(state.currentTask){
        state.canEditGeometries = canEditGeometries(state.currentTask);
      }
    },
    setCanEditGeometries: (state, action: PayloadAction<boolean>) => {
      state.canEditGeometries = action.payload;
    },
    setShowTaskModal: (state, action: PayloadAction<Boolean | undefined>) => {
      state.showTaskModal = action.payload;
    },
    setShowNotaDeCierreModal: (state, action: PayloadAction<Boolean | undefined>) => {
      state.showNotaDeCierreModal = action.payload;
    },
    setCancelToken: (state, action: PayloadAction<any>) => {
      state.filtros.cancelToken = action.payload;
    }
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder.addCase(
      getTasks.fulfilled,
      (
        state,
        action: PayloadAction<{ status: boolean; data: Task[] | unknown }>,
      ) => {
        state.task = action.payload.data as Task[];
      },
    );
    builder.addCase(
      getTaskTypeByUser.fulfilled,
      (
        state,
        action: PayloadAction<{
          status: boolean;
          data: TaskType[] | any;
        }>,
      ) => {
        state.taskTypeByUser = action.payload.data.taskType;
      },
    );
    builder.addCase(
      getUsersAssignedTo.fulfilled,
      (
        state,
        action: PayloadAction<{ status: boolean; data: User[] | unknown }>,
      ) => {
        state.usersTaskAssignedTo = action.payload.data as User[];
      },
    );
    builder.addCase(
      getUsersCreatedBy.fulfilled,
      (
        state,
        action: PayloadAction<{ status: boolean; data: User[] | unknown }>,
      ) => {
        state.usersTaskCreatedBy = action.payload.data as User[];
      },
    );
    builder.addCase(
      getTask.fulfilled,
      (
        state,
        action: PayloadAction<Task>,
      ) => {
        state.currentTask = action.payload;
      },
    );
    builder.addCase(
      cloneTask.fulfilled,
      (
        state,
        action: PayloadAction<{ status: boolean; data: Task | unknown }>,
      ) => {
        state.task = [...state.task, (action.payload.data as Task)];
      },
    );
    builder.addCase(
      createTask.fulfilled, (state, action) => {
        if (action.payload.status)
          state.currentTask = action.payload.data as Task;
      },
    );
    builder.addCase(
      updateTask.fulfilled, (state, action) => {
        if (action.payload.status)
          state.currentTask = action.payload.data as Task;
        state.notDataTask = false;
      },
    );
    builder.addCase(getTask.pending, (state) => {
        state.currentTask = undefined;
        state.notDataTask = false;
      },
    );
    /*si la peticion es rechazada, por que no existe la tarea
      se actualiza el estado state.notDataTask
    */
    builder.addCase(getTask.rejected, (state) => {
        state.notDataTask = true;
      },
    );

    builder.addCase(getAllTasksFilters.fulfilled, (state, action) => {
      state.task = action.payload.results;
      state.totalItems = action.payload.pagination.total;
    });
  },
});

export const getTasks = createAsyncThunk('get_tasks', async () => {
  return taskService.getTasks();
});

export const getTask = createAsyncThunk('get_task', async (id: number) => {
  return taskService.getTask(id);
});

export const getTasksExportExcel = createAsyncThunk(
  'get_tasks_export_excel',
  async (_: void, api) => {
    return taskService.getTasksExportExcel((api.getState() as RootState).task.filtros);
  },
);

export const createTask = createAsyncThunk('create_task', (task: Task) => {
  return taskService.createTask(task);
});

export const updateTask = createAsyncThunk('update_task', (task: Task) => {
  return taskService.updateTask(task);
});

export const getTaskTypeByUser = createAsyncThunk(
  'get_task_type_by_user',
  () => {
    return taskService.getTasksTypeByUser();
  },
);

export const getUsersAssignedTo = createAsyncThunk(
  'get_users_assigned',
  async () => {
    return taskService.getUsersAssignedTo();
  },
);

export const getUsersCreatedBy = createAsyncThunk(
  'get_users_created',
  async () => {
    return taskService.getUsersCreatedBy();
  },
);

export const deleteTask = createAsyncThunk(
  'delete_task',
  async (taskId: number) => {
    return taskService.deleteTask(taskId);
  },
);

export const cloneTask = createAsyncThunk(
  'clone_task',
  async (taskId: number) => {
    return taskService.cloneTask(taskId);
  },
);

export const exportTask = createAsyncThunk(
  'export_task',
  async (taskId: number) => {
    return TaskService.downloadKmzFile(taskId);
  },
);


export const closeTask = createAsyncThunk(
  'close_task',
  async (closeTaskParameters: { taskId: number; notaDeCierre: string }) => {
    return taskService.closeTask(closeTaskParameters.taskId, closeTaskParameters.notaDeCierre);
  },
);

export const getAllTasksFilters = createAsyncThunk(
  'getAllTasksFilters',
  async (_: void, api) => {
    return TaskService.getAllTasksFilters((api.getState() as RootState).task.filtros);
  },
);

export const {
  setTaskFilters,
  clearCurrentTask,
  setShowTaskModal,
  setShowNotaDeCierreModal,
  setDeleteTask,
  clearNotDataTask,
  refreshCanEditGeometry,
  setCanEditGeometries,
  setShowTaskCard,
  setShowFiltrosTasks,
  setCancelToken,
} = taskSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.user.value)`
export const selectTask = (state: RootState) => state.task;
export const selectShowTaskModal = (state: RootState) => state.task.showTaskModal;
export const selectShowNotaDeCierreModal = (state: RootState) => state.task.showNotaDeCierreModal;
export const selectCurrentTask = (state: RootState) => state.task.currentTask;
export const selectNotDataTask = (state: RootState) => state.task.notDataTask;
export const selectUsersAssignedTo = (state: RootState) => {
  return state.task.usersTaskAssignedTo;
};
export const selectUsersCreatedBy = (state: RootState) =>
  state.task.usersTaskCreatedBy;
export const selectTaskTypeByUser = (state: RootState) =>
  state.task.taskTypeByUser;
export const selectShowTaskCard = (state: RootState) => state.task.showTaskCard;
export const selectCancelToken = (state: RootState) => state.task.filtros.cancelToken;

export default taskSlice.reducer;
